import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Shield, Download } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Features', href: '#features', isAnchor: true },
    { name: 'How It Works', href: '#how-it-works', isAnchor: true },
    { name: 'Privacy', href: '#privacy', isAnchor: true },
    { name: 'Feedback', href: '/feedback', isAnchor: false },
  ];

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsOpen(false);
  };

  const trackNavClick = (linkName: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'click', {
        event_category: 'Navigation',
        event_label: linkName,
        value: 1
      });
    }
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'py-3 glass shadow-lg shadow-cyclara-500/5' 
          : 'py-6 bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="w-10 h-10 rounded-2xl bg-gradient-to-br from-cyclara-500 to-lavender-500
                         flex items-center justify-center shadow-lg shadow-cyclara-500/30"
            >
              <span className="text-white text-xl">🌸</span>
            </motion.div>
            <span className="text-2xl font-bold text-midnight-500 group-hover:text-cyclara-500 transition-colors">
              Cyclara
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isAnchor ? (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="text-midnight-400 hover:text-cyclara-500 font-medium transition-colors relative group cursor-pointer"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyclara-500 to-lavender-500
                                 group-hover:w-full transition-all duration-300" />
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={() => trackNavClick(link.name)}
                  className="text-midnight-400 hover:text-cyclara-500 font-medium transition-colors relative group"
                >
                  {link.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-cyclara-500 to-lavender-500
                                 group-hover:w-full transition-all duration-300" />
                </Link>
              )
            ))}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <div className="privacy-badge">
              <Shield className="w-4 h-4" />
              <span>100% Private</span>
            </div>
            <motion.a
              href="#download"
              onClick={(e) => handleAnchorClick(e, '#download')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyclara-500 to-lavender-500
                       text-white font-semibold rounded-xl shadow-lg shadow-cyclara-500/25
                       hover:shadow-xl hover:shadow-cyclara-500/30 transition-all cursor-pointer"
            >
              <Download className="w-4 h-4" />
              <span>Download Free</span>
            </motion.a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-xl hover:bg-cyclara-50 transition-colors"
          >
            {isOpen ? (
              <X className="w-6 h-6 text-midnight-500" />
            ) : (
              <Menu className="w-6 h-6 text-midnight-500" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden mt-4 overflow-hidden"
            >
              <div className="glass rounded-2xl p-6 space-y-4">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    {link.isAnchor ? (
                      <a
                        href={link.href}
                        onClick={(e) => handleAnchorClick(e, link.href)}
                        className="block py-3 px-4 rounded-xl text-midnight-500 hover:bg-cyclara-50
                                 hover:text-cyclara-500 font-medium transition-all cursor-pointer"
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        onClick={() => trackNavClick(link.name)}
                        className="block py-3 px-4 rounded-xl text-midnight-500 hover:bg-cyclara-50
                                 hover:text-cyclara-500 font-medium transition-all"
                      >
                        {link.name}
                      </Link>
                    )}
                  </motion.div>
                ))}
                <motion.a
                  href="#download"
                  onClick={(e) => handleAnchorClick(e, '#download')}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="block w-full text-center py-4 bg-gradient-to-r from-cyclara-500 to-lavender-500
                           text-white font-semibold rounded-xl cursor-pointer"
                >
                  Download Free
                </motion.a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
